import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";

export type InfringementStatus =
    | "report_ready"
    | "not_eligible"
    | "no_longer_active"
    | "capturing_data"
    | "start_capture";

export interface InfringementRow {
    domain_name: string;
    paid_presence_rate?: number;
    active_days?: number;
    total_days?: number;
    unique_ads?: number;
    title_only_count?: number;
    desc_only_count?: number;
    both_count?: number;
    search_terms?: string[];
    latest_gcs_uri?: string | null;
    status: InfringementStatus;
    days: number;
}

export interface InfringementExportRow {
    domain_name: string;
    region_name: string;
    url: string | null;
    title: string | null;
    description: string | null;
    gcs_uri: string | null;
}

const JSON_HEADERS = {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    Accept: "application/json",
};

const brandInfringement = createApi({
    baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL }),
    reducerPath: "api.brandInfringement",
    tagTypes: ["brandInfringement"],
    endpoints: (builder) => ({
        getInfringementGrid: builder.query<{ rows: InfringementRow[] }, {
            workspaceTable?: string, country?: string | null, days: number,
        }>({
            query: (body) => ({
                url: `/visibility/brand-infringement/getInfringingDomains`,
                method: "POST",
                body: JSON.stringify(body),
                headers: JSON_HEADERS,
            }),
            providesTags: ["brandInfringement"],
        }),
        getInfringementBadge: builder.query<{ count: number }, { workspaceTable?: string }>({
            query: (body) => ({
                url: `/visibility/brand-infringement/getInfringementBadgeCount`,
                method: "POST",
                body: JSON.stringify(body),
                headers: JSON_HEADERS,
            }),
        }),
        getInfringementExport: builder.query<{ rows: InfringementExportRow[] }, {
            workspaceTable?: string, country?: string | null, days: number, domain?: string,
        }>({
            query: (body) => ({
                url: `/visibility/brand-infringement/getTrademarkReportExport`,
                method: "POST",
                body: JSON.stringify(body),
                headers: JSON_HEADERS,
            }),
        }),
        startCapture: builder.mutation<{ created: number }, {
            workspaceTable?: string, domain: string, searchTerms: string[],
            country?: string | null, countryCode?: string | null,
        }>({
            query: (body) => ({
                url: `/visibility/brand-infringement/startInfringementCapture`,
                method: "POST",
                body: JSON.stringify(body),
                headers: JSON_HEADERS,
            }),
            invalidatesTags: ["brandInfringement"],
        }),
        exportTrademarkReport: builder.mutation<{ id: string }, {
            workspaceTable?: string, country?: string | null, days: number, domain: string,
        }>({
            query: (body) => ({
                url: `/visibility/brand-infringement/exportTrademarkReport`,
                method: "POST",
                body: JSON.stringify(body),
                headers: JSON_HEADERS,
            }),
        }),
    }),
});

export const {
    useGetInfringementGridQuery,
    useGetInfringementBadgeQuery,
    useLazyGetInfringementExportQuery,
    useStartCaptureMutation,
    useExportTrademarkReportMutation,
} = brandInfringement;

export default brandInfringement;
